import React from 'react';
import { Text, Heading } from '@/components/ui/design-system';

export const TermsOfServiceContent = () => (
  <>
    <Heading level={2} className="heading-underline-animated pb-2">Introduction</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Welcome to PAVISHA. These Terms of Service govern your use of our website, products, and services. By accessing or using PAVISHA, you agree to be bound by these Terms.
    </Text>
    <Text as="blockquote" className="mt-4 mb-5 pl-4 border-l-4 border-primary bg-primary/10 py-2 italic">
      Please read these Terms carefully before using our platform. If you do not agree with any part of these Terms, you may not use our services.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2">Definitions</Heading>
    <Text size="lg" className="mt-4 mb-5">To ensure clarity throughout these Terms, please note the following definitions:</Text>
    <Text size="lg" className="mt-4 mb-5"><strong>Platform:</strong> The PAVISHA website, mobile applications, and related services.</Text>
    <Text size="lg" className="mt-4 mb-5"><strong>Services:</strong> Any products, services, content, features, technologies, or functions offered by PAVISHA.</Text>
    <Text size="lg" className="mt-4 mb-5"><strong>User:</strong> Any individual who accesses or uses the Platform, whether registered or unregistered.</Text>
    <Text size="lg" className="mt-4 mb-5"><strong>Content:</strong> All information, text, images, data, links, software, or other material accessible through the Platform.</Text>

    <Heading level={2} className="heading-underline-animated pb-2">Account Registration</Heading>
    <Heading level={3} className="heading-underline-animated pb-2 mt-8">Creating an Account</Heading>
    <Text size="lg" className="mt-4 mb-5">
      To access certain features of our Platform, you may need to register for an account. When you register, you agree to provide accurate, current, and complete information about yourself.
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>You must be at least 18 years old to create an account.</li>
      <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
      <li>You are responsible for all activities that occur under your account.</li>
      <li>You must notify us immediately of any unauthorized use of your account.</li>
    </ul>
    <Heading level={3} className="heading-underline-animated pb-2 mt-8">Account Termination</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We reserve the right to suspend or terminate your account at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2">User Conduct</Heading>
    <Text size="lg" className="mt-4 mb-5">When using our Platform, you agree not to:</Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>Violate any applicable laws or regulations</li>
      <li>Infringe upon the rights of others</li>
      <li>Use the Platform to distribute unsolicited commercial messages</li>
      <li>Upload or transmit viruses, malware, or other malicious code</li>
      <li>Attempt to gain unauthorized access to the Platform or any related systems</li>
      <li>Engage in any activity that could disable, overburden, or impair the proper functioning of the Platform</li>
    </ul>

    <Heading level={2} className="heading-underline-animated pb-2">Intellectual Property</Heading>
    <Text size="lg" className="mt-4 mb-5">
      All content on the Platform, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, and software, is the property of PAVISHA or its content suppliers and is protected by international copyright laws.
    </Text>
    <Text as="blockquote" className="mt-4 mb-5 pl-4 border-l-4 border-primary bg-primary/10 py-2 italic">
      You may not use, reproduce, modify, or distribute any content from our Platform without prior written permission from PAVISHA.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2">Privacy</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Your privacy is important to us. Please refer to our <a href="/privacy-policy">Privacy Policy</a> for information about how we collect, use, and disclose information about you.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2">Disclaimers</Heading>
    <Text size="lg" className="mt-4 mb-5">
      The Platform and all Services are provided "as is" and "as available" without any warranty of any kind, express or implied. To the maximum extent permitted by applicable law, PAVISHA disclaims all warranties, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2">Limitation of Liability</Heading>
    <Text size="lg" className="mt-4 mb-5">
      To the maximum extent permitted by applicable law, in no event shall PAVISHA be liable for any special, incidental, indirect, consequential, or punitive damages whatsoever arising out of or in connection with your use of or inability to use the Platform or Services.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2">Changes to Terms</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We may modify these Terms at any time. If we make material changes, we will notify you by email or by posting a notice on our Platform. Your continued use of the Platform after such notification constitutes your acceptance of the modified Terms.
    </Text>
  </>
);
